import ReportTable from "../../ReportTable.component";
import { useEffect, useMemo, useRef, useState } from "react";
import ReportService from "../../../../services/reports/Report.service";
import BaseReport from "../../BaseReport.component";
import { MoneyDataItem, PercentDataItem } from "../../../../interfaces/reports/BaseReport.interface";
import EpochMonthService from "../../../../services/EpochMonth.service";
import MonthSelector from "../../../../tailwindComponents/ui/dates/month-selector.component";
import CustomReportService from "../../../../services/reports/CustomReport.service";

interface TootsiesGlobalAnalyticsProps {
  clientId: string | number;
  epochMonth: number | null;
}

const TODAY_EPOCH = EpochMonthService.getTodayEpochMonth();

const brandHierarchyGa: Record<string, string[]> = {
  "By Brand": ["brand"],
  "Location by Brand": ["locationCode", "brand"],
  "Location by Class by Brand": ["locationCode", "classCode", "brand"],
  "Class by Brand": ["classCode", "brand"],
  "Class by Brand by Location": ["classCode", "brand", "locationCode"],
  "Category by Class by Brand": ["category", "classCode", "brand"],
  "Category by Brand": ["category", "brand"],
  "Category by Location by Brand": ["category", "locationCode", "brand"],
  "Location by Category by Brand": ["locationCode", "category", "brand"],
  "Category by Brand by Location": ["category", "brand", "locationCode"],
  "By Location": ["locationCode"],
  "Location By Class": ["locationCode", "classCode"],
  "Location By Category": ["locationCode", "category"],
  "Location by Category by Class": ["locationCode", "category", "classCode"],
  "By Class": ["classCode"],
  "Class by Location": ["classCode", "locationCode"],
  "By Category": ["category"],
  "Category by Location": ["category", "locationCode"],
  "Category by Class": ["category", "classCode"],
  "Category by Class by Location": ["category", "classCode", "locationCode"],
};

// Parsed data type for Tootsies (plan fields omitted)
interface TootsiesParsedDataItem {
  brand?: string;
  locationCode: string; // required for hierarchy
  locationName?: string;
  classCode: string; // required for hierarchy
  className?: string;
  category: string; // required for hierarchy
  categorySortOrder?: number | null;
  lastMonthSales: MoneyDataItem;
  last90MarkdownPct: PercentDataItem;
  last90InflowRetail: MoneyDataItem;
  last90InflowCost: MoneyDataItem;
  last90Sales: MoneyDataItem;
  salesL3Ly: MoneyDataItem;
  thisMonthInventory: MoneyDataItem;
  nonNegativeInflowDeviation: MoneyDataItem;
  inflowPlusDeviation: MoneyDataItem;
  inflowPlusDeviationCost: MoneyDataItem;
  onOrderCost: MoneyDataItem;
  onOrderRetail: MoneyDataItem;
  markdownsL3: MoneyDataItem;
  salesL12: MoneyDataItem;
}

const processRawTootsies = (raw: any[], showOnlyActuals: boolean = true): TootsiesParsedDataItem[] => {
  if (!raw || raw.length === 0) return [];
  return raw
    .filter((r) => {
      // When showing only actuals, keep rows with any actual activity: sales OR markdowns OR inventory OR receiving > 0
      if (!showOnlyActuals) return true;
      const sales = Number(r?.salesLm ?? r?.sales ?? 0);
      const markdowns = Number(r?.markdownsL3 ?? r?.markdowns ?? 0);
      const inventory = Number(r?.inventoryBomRm ?? r?.inventory ?? r?.invCurrent ?? r?.invAvg ?? 0);
      const receiving = Number(r?.inflowRetailL3 ?? r?.inflowCostL3 ?? r?.receivedRetailAcrossLocations ?? r?.receivedCostAcrossLocations ?? r?.inflowRetail ?? r?.inflowCost ?? 0);
      return (sales > 0) || (markdowns > 0) || (inventory > 0) || (receiving > 0);
    })
    .map((r) => {
      return {
        locationCode: r.locationCode ?? "",
        locationName: r.locationName,
        classCode: r.classCode ?? "",
        className: r.className,
        categorySortOrder: r.categorySortOrder ?? null,
        category: r.category ?? 'N/A',
        brand: r.brand ?? 'Non-Branded',

        // Skip parsing all plan fields (lm sales plan, sales plan, inventory plan)
        lastMonthSales: new MoneyDataItem(r.salesLm),
        last90MarkdownPct: new PercentDataItem(0),
        last90InflowRetail: new MoneyDataItem(r.inflowRetailL3),
        last90InflowCost: new MoneyDataItem(r.inflowCostL3),
        last90Sales: new MoneyDataItem(r.salesL3),
        salesL3Ly: new MoneyDataItem(r.salesL3Ly ?? 0),
        thisMonthInventory: new MoneyDataItem(r.inventoryBomRm),
        nonNegativeInflowDeviation: new MoneyDataItem(r.inflowPlusDeviation < 0 ? 0 : r.inflowPlusDeviation),
        inflowPlusDeviation: new MoneyDataItem(r.inflowPlusDeviation),
        inflowPlusDeviationCost: new MoneyDataItem(r.inflowPlusDeviationCost),
        onOrderCost: new MoneyDataItem(r.onOrderCost),
        onOrderRetail: new MoneyDataItem(r.onOrderRetail),
        markdownsL3: new MoneyDataItem(r.markdownsL3 ?? 0),
        salesL12: new MoneyDataItem(r.salesL12 ?? 0),
      } as TootsiesParsedDataItem;
    });
};

const reapplyPercentCalcs = (data: TootsiesParsedDataItem[]) => {
  if (!data || data.length === 0) return data;
  return data.map((item) => {
    const mdPct = item.last90Sales?.value && item.markdownsL3?.value
      ? (Number(item.markdownsL3.value) / Number(item.last90Sales.value)) * 100
      : 0;
    return {
      ...item,
      last90MarkdownPct: new PercentDataItem(mdPct),
    } as TootsiesParsedDataItem;
  });
};

// Columns to display (exclude LM Sales Plan, Sales Plan, Inventory Plan, and Inflow + Deviation)
const DATA_COLUMNS = [
  { key: "lmSales", label: "LM - Sales" },
  { key: "markdownPct", label: "Markdown %" },
  { key: "inflowOverTime", label: "Inflow Over Time" },
  { key: "salesOverTime", label: "Sales Over Time" },
  { key: "salesOverTimeVsLy", label: "Sales Over Time vs LY" },
  { key: "inventory", label: "Inventory" },
  { key: "invPctTotal", label: "Inv % of Group" },
  { key: "lmSalesPctGroup", label: "LM Sales % of Gr." },
  { key: "lySalesPctGroup", label: "LY Sales % of Gr." },
  { key: "inflowPctGroup", label: "Inflow % of Gr." },
  { key: "onOrder", label: "On Order" },
];

const TootsiesGlobalAnalyticsReport = ({ clientId, epochMonth }: TootsiesGlobalAnalyticsProps) => {
  const dataRef = useRef<any[]>([]);
  const parentDataRef = useRef<any[]>([]);
  const [parsedData, setParsedData] = useState<TootsiesParsedDataItem[]>([]);
  const [sortedData, setSortedData] = useState<TootsiesParsedDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hierarchy, setHierarchy] = useState<string[] | null>(["locationCode", "classCode", "brand"]);
  const [startEpochMonth, setStartEpochMonth] = useState<number>(epochMonth ?? TODAY_EPOCH);
  const [endEpochMonth, setEndEpochMonth] = useState<number>(epochMonth ?? TODAY_EPOCH + 3);
  const [monthsBack, setMonthsBack] = useState<number>(3);
  const [pricingType, setPricingType] = useState<"Retail" | "Cost">("Retail");
  const [showOnlyActuals, setShowOnlyActuals] = useState<boolean>(true);

  const [locationFilter, setLocationFilter] = useState<string[]>([]);
  const [classFilter, setClassFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

  // Build hierarchy header columns
  let hierarchyColumns: Array<{ name: string; sortable?: boolean; customClassName?: string }> = [];
  for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
    hierarchyColumns.push({
      name: "",
      sortable: false,
      customClassName: i === 0
        ? "px-2 py-2 bg-gray-500 text-white"
        : "px-2 py-2 bg-purple-100 border-purple-700 text-purple-800"
    });
  }

  const dataColumns = DATA_COLUMNS.map((c) => ({ name: c.label, sortable: true, customClassName: "px-2 py-2" }));
  const columns = [
    ...hierarchyColumns,
    ...dataColumns,
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const args = { clientId, startEpochMonth, endEpochMonth, monthsBack };
        const response = await CustomReportService.getTootsiesGlobalAnalytics(args);
        if (response) {
          dataRef.current = response.data ?? [];
          parentDataRef.current = response.parentLevelData ?? [];

          const childParsed = processRawTootsies(response.data ?? [], showOnlyActuals);
          setParsedData(childParsed);

          const initial = ReportService.groupByHierarchy(childParsed as any, hierarchy ?? ["locationCode", "classCode"]) as any as TootsiesParsedDataItem[];
          setSortedData(reapplyPercentCalcs(initial));
        } else {
          dataRef.current = [];
          parentDataRef.current = [];
          setParsedData([]);
          setSortedData([]);
        }
      } catch (e) {
        setParsedData([]);
        setSortedData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId, startEpochMonth, endEpochMonth, monthsBack, showOnlyActuals]);

  useEffect(() => {
    setEndEpochMonth(startEpochMonth + 3);
  }, [startEpochMonth]);

  useEffect(() => {
    const source = parsedData;
    if (source.length > 0) {
      const newSorted = ReportService.sortAndFilterData(
        source as any[],
        hierarchy ?? ["locationCode", "classCode"],
        locationFilter,
        classFilter,
        categoryFilter
      );
      setSortedData(reapplyPercentCalcs(newSorted as TootsiesParsedDataItem[]));
    } else {
      setSortedData([]);
    }
  }, [hierarchy, parsedData, locationFilter, classFilter, categoryFilter]);

  const tableData = useMemo(() => {
    const isCost = pricingType === "Cost";

    let grandTotal = { inventory: 0, salesLm: 0, inflowDev: 0, salesL12: 0, salesL3: 0, salesL3ly: 0 };
    let groupTotal1 = { inventory: 0, salesLm: 0, inflowDev: 0, salesL12: 0, salesL3: 0, salesL3ly: 0 };
    let groupTotal2 = { inventory: 0, salesLm: 0, inflowDev: 0, salesL12: 0, salesL3: 0, salesL3ly: 0 };

    return sortedData.map((item) => {
      // Use base naming logic to render hierarchy labels
      const labels = ReportService.hierarchyItemToVerticalDisplayNames(item as any, hierarchy ?? ["locationCode", "classCode"]);
      const totalCount = labels.filter(label => label.includes("TOTAL")).length;
      const hierarchyLength = hierarchy?.length ?? 2;

      if (totalCount === hierarchyLength) {
        grandTotal = {
          inventory: Number(item.thisMonthInventory?.value) || 0,
          salesLm: Number(item.lastMonthSales?.value) || 0,
          inflowDev: Number(item.nonNegativeInflowDeviation?.value) || 0,
          salesL12: Number(item.salesL12?.value) || 0,
          salesL3: Number(item.last90Sales?.value) || 0,
          salesL3ly: Number(item.salesL3Ly?.value) || 0
        };
      } else if (totalCount === hierarchyLength - 1) {
        groupTotal1 = {
          inventory: Number(item.thisMonthInventory?.value) || 0,
          salesLm: Number(item.lastMonthSales?.value) || 0,
          inflowDev: Number(item.nonNegativeInflowDeviation?.value) || 0,
          salesL12: Number(item.salesL12?.value) || 0,
          salesL3: Number(item.last90Sales?.value) || 0,
          salesL3ly: Number(item.salesL3Ly?.value) || 0
        };
      } else if (totalCount === hierarchyLength - 2 && hierarchyLength >= 3) {
        groupTotal2 = {
          inventory: Number(item.thisMonthInventory?.value) || 0,
          salesLm: Number(item.lastMonthSales?.value) || 0,
          inflowDev: Number(item.nonNegativeInflowDeviation?.value) || 0,
          salesL12: Number(item.salesL12?.value) || 0,
          salesL3: Number(item.last90Sales?.value) || 0,
          salesL3ly: Number(item.salesL3Ly?.value) || 0
        };
      }

      let useTotal = grandTotal;
      if (totalCount === 0) {
        useTotal = hierarchyLength === 1 ? grandTotal :
          hierarchyLength === 2 ? groupTotal1 :
          (groupTotal2.inventory ? groupTotal2 : groupTotal1);
      } else if (totalCount === hierarchyLength - 1) {
        useTotal = grandTotal;
      } else if (totalCount === hierarchyLength - 2 && hierarchyLength >= 3) {
        useTotal = groupTotal1;
      }

      const totalsCtx = { useTotal };

      const values = DATA_COLUMNS.map((col) => {
        switch (col.key) {
          case "lmSales":
            return item.lastMonthSales.formatted;
          case "markdownPct":
            return item.last90MarkdownPct?.formatted || "";
          case "inflowOverTime":
            return isCost ? item.last90InflowCost.formatted : item.last90InflowRetail.formatted;
          case "salesOverTime":
            return item.last90Sales.formatted;
          case "salesOverTimeVsLy": {
            const salesL3Val = Number(item.last90Sales?.value) || 0;
            const salesL3LyVal = Number(item.salesL3Ly?.value) || 0;
            const pct = salesL3LyVal && salesL3Val ? ((salesL3Val - salesL3LyVal) / salesL3LyVal) * 100 : 0;
            return pct ? `${pct.toFixed(0)}%` : "";
          }
          case "inventory":
            return item.thisMonthInventory.formatted;
          case "invPctTotal": {
            const invVal = Number(item.thisMonthInventory?.value) || 0;
            const invPct = totalsCtx.useTotal.inventory ? (invVal / totalsCtx.useTotal.inventory) * 100 : 0;
            return invPct ? `${invPct.toFixed(0)}%` : "0%";
          }
          case "lmSalesPctGroup": {
            const salesVal = Number(item.lastMonthSales?.value) || 0;
            const pct = totalsCtx.useTotal.salesLm ? (salesVal / totalsCtx.useTotal.salesLm) * 100 : 0;
            return pct ? `${pct.toFixed(0)}%` : "";
          }
          case "lySalesPctGroup": {
            const last12SalesVal = Number(item.salesL12?.value) || 0;
            const pct = totalsCtx.useTotal.salesL12 ? (last12SalesVal / totalsCtx.useTotal.salesL12) * 100 : 0;
            return pct ? `${pct.toFixed(0)}%` : "";
          }
          case "inflowPctGroup": {
            const inflowVal = Number(item.nonNegativeInflowDeviation?.value) || 0;
            const pct = totalsCtx.useTotal.inflowDev ? (inflowVal / totalsCtx.useTotal.inflowDev) * 100 : 0;
            return pct ? `${pct.toFixed(0)}%` : "";
          }
          case "onOrder":
            return isCost ? item.onOrderCost.formatted : item.onOrderRetail.formatted;
          default:
            return "";
        }
      });

      return [
        ...labels,
        ...values,
      ];
    });
  }, [sortedData, hierarchy, pricingType]);

  const rowClassName = (rowIndex: number) => {
    if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
    return ReportService.getRowClassName(tableData[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center">
      <h2 className="text-2xl font-bold mb-4">{`${EpochMonthService.epochMonthToMMMYY(startEpochMonth ?? TODAY_EPOCH - 12)} to ${EpochMonthService.epochMonthToMMMYY(endEpochMonth ?? TODAY_EPOCH)} Global Analytics (Tootsies)`}</h2>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="h-full max-w-[1700px]">
          <BaseReport
            title={`${EpochMonthService.epochMonthToMMMYY(startEpochMonth ?? TODAY_EPOCH - 12)} to ${EpochMonthService.epochMonthToMMMYY(endEpochMonth ?? TODAY_EPOCH)} Global Analytics (Tootsies)`}
            tableColumns={columns.map((c) => c.name)}
            tableData={tableData}
            fileName={`Global_Analytics_Tootsies_${new Date().toLocaleDateString()}`}
            setHierarchy={setHierarchy}
            hierarchy={hierarchy}
            hierarchyOptions={brandHierarchyGa}
            parsedData={parsedData as any}
            filteredLocationCodes={locationFilter}
            filteredClassCodes={classFilter}
            setFilteredLocationCodes={setLocationFilter}
            setFilteredClassCodes={setClassFilter}
            filteredCategories={categoryFilter}
            setFilteredCategories={setCategoryFilter}
            onApplyFilters={() => {
              setParsedData(processRawTootsies(dataRef.current, showOnlyActuals));
            }}
            hasSecondaryToolbar={true}
          />
          <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
            <div className="flex flex-col items-start">
              <span className="text-xs font-semibold text-slate-600">Start Period</span>
              <MonthSelector epochMonth={startEpochMonth} setEpochMonth={setStartEpochMonth} minMonth={TODAY_EPOCH - 12} maxMonth={TODAY_EPOCH} />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs font-semibold text-slate-600">End Period</span>
              <MonthSelector epochMonth={endEpochMonth} setEpochMonth={setEndEpochMonth} minMonth={startEpochMonth} maxMonth={startEpochMonth + 11} />
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs font-semibold text-slate-600">Months Back</span>
              <select
                value={monthsBack}
                onChange={e => {
                  const val = parseInt(e.target.value, 10);
                  setMonthsBack(val);
                }}
                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <option key={m} value={m}>{m} Months</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col items-start">
              <span className="text-xs font-semibold text-slate-600">Pricing Type</span>
              <select
                value={pricingType}
                onChange={e => {
                  const val = e.target.value;
                  setPricingType(val === 'Retail' ? 'Retail' : 'Cost');
                }}
                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
              >
                <option value="Retail">Retail</option>
                <option value="Cost">Cost</option>
              </select>
            </div>

            {/* Toggle: Show only brands with actuals  */}
            <div className="flex flex-col items-start">
              <span className="text-xs font-semibold text-slate-600">Brands</span>
              <select
                value={showOnlyActuals ? 'actuals' : 'all'}
                onChange={e => {
                  const val = e.target.value;
                  const onlyActuals = val === 'actuals';
                  setShowOnlyActuals(onlyActuals);
                  // Re-parse with new toggle and reapply sorting
                  const childParsed = processRawTootsies(dataRef.current, onlyActuals);
                  setParsedData(childParsed);
                  const newSorted = ReportService.sortAndFilterData(
                    childParsed as any[],
                    hierarchy ?? ["locationCode", "classCode"],
                    locationFilter,
                    classFilter,
                    categoryFilter
                  );
                  setSortedData(reapplyPercentCalcs(newSorted as TootsiesParsedDataItem[]));
                }}
                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
              >
                <option value="actuals">Only with actuals</option>
                <option value="all">All brands</option>
              </select>
            </div>
          </div>

          <ReportTable
            columns={columns}
            data={tableData}
            rowClassName={rowClassName}
            defaultSortColumn={null}
            defaultSortDirection="asc"
            headerClassName="bg-slate-700 !text-white !text-center py-2 px-1"
            toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
            toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
            stickyHeader={true}
            virtualize={true}
            batchSize={300}
          />
        </div>
      )}
    </div>
  );
};

export default TootsiesGlobalAnalyticsReport;